// components/settings/DeleteAccountModal.tsx
import React, { useState } from "react";
import {
  X,
  AlertTriangle,
  Trash2,
  Loader2,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

interface DeleteAccountModalProps {
  onClose: () => void;
  onConfirm: (reason: string, feedback: string) => Promise<boolean>;
  isPending: boolean;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  onClose,
  onConfirm,
  isPending,
}) => {
  const [step, setStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [confirmText, setConfirmText] = useState("");
  //   const [isDeleting, setIsDeleting] = useState(false);

  const reasons = [
    { id: "not_using", label: "I'm not using the platform anymore" },
    { id: "found_alternative", label: "I found a better alternative" },
    { id: "privacy", label: "Privacy concerns" },
    { id: "too_complicated", label: "The platform is too complicated" },
    { id: "not_enough_tasks", label: "Not enough tasks available" },
    { id: "payment_issues", label: "Issues with payments" },
    { id: "other", label: "Other reason" },
  ];

  const handleNext = () => {
    if (step === 1 && selectedReason) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;

    const success = await onConfirm(selectedReason, feedback);
    if (success) {
      // Modal will close due to redirect
    }
  };

  const renderStep1 = () => (
    <div>
      <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-6">
        <AlertTriangle className="w-8 h-8 text-red-600" />
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
        We're sorry to see you go
      </h3>
      <p className="text-gray-500 text-sm mb-6 text-center">
        Before you delete your account, please let us know why you're leaving.
      </p>

      <div className="space-y-2">
        {reasons.map((reason) => (
          <button
            key={reason.id}
            onClick={() => setSelectedReason(reason.id)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
              selectedReason === reason.id
                ? "border-[#8B52FF] bg-[#8B52FF]/5"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedReason === reason.id
                  ? "border-[#8B52FF] bg-[#8B52FF]"
                  : "border-gray-300"
              }`}
            >
              {selectedReason === reason.id && (
                <CheckCircle className="w-3 h-3 text-white" />
              )}
            </div>
            <span
              className={`text-sm ${
                selectedReason === reason.id
                  ? "text-[#8B52FF] font-medium"
                  : "text-gray-700"
              }`}
            >
              {reason.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
        Any additional feedback?
      </h3>
      <p className="text-gray-500 text-sm mb-6 text-center">
        Your feedback helps us improve the platform for others.
      </p>

      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="Tell us more about your experience... (optional)"
        rows={5}
        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all resize-none"
      />

      {/* What you'll lose */}
      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
        <p className="font-medium text-red-800 mb-2 text-sm">
          What you'll lose by deleting your account:
        </p>
        <ul className="text-sm text-red-700 space-y-1">
          <li>• All pending earnings ($0.00)</li>
          <li>• Task history and performance data</li>
          <li>• Certifications and badges earned</li>
          <li>• Account cannot be recovered</li>
        </ul>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-6">
        <Trash2 className="w-8 h-8 text-red-600" />
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
        Confirm Account Deletion
      </h3>
      <p className="text-gray-500 text-sm mb-6 text-center">
        This action is permanent and cannot be undone. Type{" "}
        <span className="font-mono font-bold text-red-600">DELETE</span> to
        confirm.
      </p>

      <input
        type="text"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
        placeholder="Type DELETE to confirm"
        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 focus:outline-none transition-all text-center font-mono text-lg"
      />

      {confirmText && confirmText !== "DELETE" && (
        <p className="text-red-500 text-sm mt-2 text-center">
          Please type DELETE exactly to confirm
        </p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-red-600">Delete Account</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    s < step
                      ? "bg-red-500 text-white"
                      : s === step
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {s < step ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-8 h-1 rounded-full transition-all ${
                      s < step ? "bg-red-500" : "bg-gray-100"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
          {step > 1 ? (
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          )}

          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={step === 1 && !selectedReason}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleDelete}
              disabled={confirmText !== "DELETE" || isPending}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete My Account
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
